import type { AgentProgressEvent } from '~/components/AiChat/AgentTimeline/types';

import type { ProgressEventHandlers, ProgressMetadata, ProgressRowStatus } from './types';
import { PROGRESS_ROW_STATUS } from './types';

export const getProgressMetadata = <T extends ProgressMetadata = ProgressMetadata>(event: AgentProgressEvent): T =>
  (event.metadata ?? {}) as T;

/** Folds a run's events into `state` in order, through each timeline's handler per event kind. */
export const foldProgressEvents = <S, M extends ProgressMetadata = ProgressMetadata>(
  events: AgentProgressEvent[],
  state: S,
  handlers: ProgressEventHandlers<S, M>
): S => {
  events.forEach((event) => {
    const meta = getProgressMetadata<M>(event);
    if (meta.kind) handlers[meta.kind]?.(state, event, meta);
  });
  return state;
};

/**
 * A row's status from where the run has got to. `step_started(i)` implies every earlier row is done,
 * which lets a dropped or skipped step self-heal; a failure keeps the rows after it pending.
 */
export const resolveRowStatus = ({
  position,
  activeIndex,
  isDone,
  failedIndex,
}: {
  position: number;
  activeIndex: number;
  isDone: boolean;
  failedIndex?: number;
}): ProgressRowStatus => {
  if (failedIndex !== undefined) {
    if (position === failedIndex) return PROGRESS_ROW_STATUS.ERROR;
    return position < failedIndex ? PROGRESS_ROW_STATUS.COMPLETED : PROGRESS_ROW_STATUS.PENDING;
  }
  if (isDone || position < activeIndex) return PROGRESS_ROW_STATUS.COMPLETED;
  return position === activeIndex ? PROGRESS_ROW_STATUS.IN_PROGRESS : PROGRESS_ROW_STATUS.PENDING;
};
